// Pure formatting + date helpers for the SPA.
//
// First extraction of the src/app.jsx decomposition (the file is ~30k lines,
// far over the 500-LOC standard). These are pure functions with no React or
// app-internal dependencies, so they move cleanly and are safe to unit-test
// in isolation — and every component extracted later can import them from
// here instead of relying on app.jsx module scope.

// ISO timestamp → "3:07 PM" (local). Empty string on null/invalid input.
export function fmtTime(isoStr) {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
}

// Render a minute-count as "Xm" / "Xh" / "Xh Ym". Used for late/early
// clock displays in TimecardReviewPanel + MyTimeCardView. "183m" is hard
// to read; "3h 3m" is what the manager actually needs to see.
export function fmtMins(m) {
  const n = Math.max(0, Math.round(Number(m) || 0));
  if (n < 60) return `${n}m`;
  const h = Math.floor(n / 60);
  const r = n % 60;
  return r === 0 ? `${h}h` : `${h}h ${r}m`;
}

// UTC ms representing midnight (00:00) in America/New_York for a YMD.
// DST-safe — probes NY offset by formatting noon UTC and reading back the
// hour. Mirrors the server's nyMidnightOfYmd in timecard-routes.js. Used
// to compute when a work week has fully ended so certify becomes available.
export function nyMidnightOfYmd(ymd) {
  const yyyy = +ymd.slice(0, 4), mm = +ymd.slice(5, 7), dd = +ymd.slice(8, 10);
  const probe = new Date(Date.UTC(yyyy, mm - 1, dd, 12, 0));
  const fmt = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', hour12: false });
  const nyHourAtNoonUtc = parseInt(fmt.format(probe), 10);
  const offsetHours = 12 - nyHourAtNoonUtc;
  return Date.UTC(yyyy, mm - 1, dd, offsetHours, 0);
}

// Money + number formatters. `—` (em dash) is the house null placeholder.
export function fmtUsd(n)  { if (n == null) return '—'; return '$' + Math.round(n).toLocaleString(); }
export function fmtUsd2(n) { if (n == null) return '—'; return '$' + n.toFixed(2); }
export function fmtNum(n)  { if (n == null) return '—'; return Number(n).toLocaleString(); }

// Signed percentage delta, e.g. "+3.2%" / "-1.0%". One decimal.
export function fmtDelta(n) {
  if (n == null) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

// Round 30: colorize WoW deltas — green for positive, red for negative,
// neutral grey for zero/null.
export function deltaColor(n) {
  if (n == null || n === 0) return '#757575';
  return n > 0 ? '#2e7d32' : '#c62828';
}

// Parse "HH:MM" 24h or "h:MM AM/PM" → minutes since midnight (null if unparseable).
export function timeToMinutes(s) {
  if (!s) return null;
  const m24 = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (m24) return Number(m24[1]) * 60 + Number(m24[2]);
  const m12 = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(s);
  if (m12) {
    let h = Number(m12[1]) % 12;
    if (m12[3].toUpperCase() === 'PM') h += 12;
    return h * 60 + Number(m12[2]);
  }
  return null;
}

// "1:30p" compact clock. Returns the input unchanged if unparseable.
export function fmtClock(s) {
  if (!s) return '';
  const mins = timeToMinutes(s);
  if (mins == null) return s;
  const h24 = Math.floor(mins / 60);
  const m = mins % 60;
  const am = h24 < 12;
  const h = h24 === 0 ? 12 : (h24 > 12 ? h24 - 12 : h24);
  return `${h}:${String(m).padStart(2, '0')}${am ? 'a' : 'p'}`;
}

// Like fmtClock but drops ":00" for whole hours and uses full "am"/"pm"
// — the form CJ asked for on Build Schedule NA badges ("1pm" not "1:00p").
export function fmtClockNa(s) {
  if (!s) return '';
  const mins = timeToMinutes(s);
  if (mins == null) return s;
  const h24 = Math.floor(mins / 60);
  const m = mins % 60;
  const am = h24 < 12;
  const h = h24 === 0 ? 12 : (h24 > 12 ? h24 - 12 : h24);
  return `${h}${m ? ':' + String(m).padStart(2, '0') : ''}${am ? 'am' : 'pm'}`;
}

// A Date → 'YYYY-MM-DD' in America/New_York (the business day).
export function ymdLocal(d) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);
}

// 'YYYY-MM-DD' → "Mon D, YYYY" (noon-UTC anchored so the date doesn't slip).
export function fmtDate(d) {
  if (!d) return '—';
  return new Date(d + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Localized dollar amount with no cents rounding, `—` for null.
export function fmtMoney(n) { return n != null ? `$${Number(n).toLocaleString()}` : '—'; }

// Today as 'YYYY-MM-DD' in Eastern — the timezone Empire operates in.
//
// This used to be `new Date().toISOString().slice(0, 10)`, which is UTC. A
// browser in RI is ET, so from 8pm until midnight it returned TOMORROW: date
// inputs defaulted a day ahead and "is this today?" comparisons were wrong
// for the four hours staff most often use the app. See shared/et-date.js.
const ET_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
});
export function todayStr() { return ET_DAY.format(new Date()); }

// Whole days between two 'YYYY-MM-DD' dates (noon-UTC anchored). Used by the
// compliance surfaces for due-date countdowns.
export function daysBetween(a, b) {
  const ms = new Date(b + 'T12:00:00Z') - new Date(a + 'T12:00:00Z');
  return Math.round(ms / 86400000);
}

// ISO timestamp → { rel, abs, stale } for activity/last-seen rows. `stale`
// flags 14+ days old (render in red). Absolute time in America/New_York.
export function formatActivityTime(iso) {
  if (!iso) return { rel: 'Never', abs: '—', stale: true };
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return { rel: '—', abs: '—', stale: false };
  const ms = Date.now() - d.getTime();
  const min = Math.round(ms / 60000);
  let rel;
  if (min < 1) rel = 'just now';
  else if (min < 60) rel = `${min} min ago`;
  else if (min < 60 * 24) rel = `${Math.round(min / 60)} hr ago`;
  else rel = `${Math.round(min / (60 * 24))} day(s) ago`;
  const abs = d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
    timeZone: 'America/New_York',
  });
  const stale = min >= 60 * 24 * 14;
  return { rel, abs, stale };
}
